class AnalyticsService {
  private sessionId: string;
  private apiBaseUrl: string;
  private startTime: number;
  private pageStartTime: number;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.apiBaseUrl = 'https://browntech.co/bti-admin-panel/public/api'; // Production API URL
    this.startTime = Date.now();
    this.pageStartTime = Date.now();
    
    // Only initialize tracking in production
    if (process.env.NODE_ENV === 'production') {
      this.initializeTracking();
    }
  }

  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private initializeTracking(): void {
    // Track page view on load
    this.trackPageView();

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackTimeSpent();
      } else if (document.visibilityState === 'visible') {
        this.pageStartTime = Date.now();
      }
    });

    // Track before unload
    window.addEventListener('beforeunload', () => {
      this.trackTimeSpent();
    });

    // Track clicks
    document.addEventListener('click', (event) => {
      this.trackClick(event);
    });

    // Track scroll events
    let scrollTimeout: NodeJS.Timeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        this.trackScroll();
      }, 1000);
    });
  }

  private async sendAnalytics(endpoint: string, data: any): Promise<void> {
    // Skip analytics in development mode
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    
    try {
      await fetch(`${this.apiBaseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          page_url: window.location.href,
          referrer_url: document.referrer,
          ...data
        })
      });
    } catch (error) {
      // Silently fail in production to avoid console errors
    }
  }

  public trackPageView(): void {
    if (process.env.NODE_ENV !== 'production') return;
    this.pageStartTime = Date.now();
    this.sendAnalytics('/analytics/page-view', {});
  }

  public trackClick(event: MouseEvent): void {
    if (process.env.NODE_ENV !== 'production') return;
    const target = event.target as HTMLElement;
    const clickData = {
      element_id: target.id || null,
      element_class: target.className || null,
      element_text: target.textContent?.trim().substring(0, 100) || null,
      click_position: {
        x: event.clientX,
        y: event.clientY
      }
    };
    this.sendAnalytics('/analytics/click', clickData);
  }

  public trackScroll(): void {
    if (process.env.NODE_ENV !== 'production') return;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);
    
    this.sendAnalytics('/analytics/scroll', {
      scroll_percentage: scrollPercentage
    });
  }

  public trackTimeSpent(): void {
    if (process.env.NODE_ENV !== 'production') return;
    const timeSpent = Math.round((Date.now() - this.pageStartTime) / 1000);
    if (timeSpent > 0) {
      this.sendAnalytics('/analytics/track', {
        time_spent: timeSpent
      });
    }
  }

  public trackCustomEvent(eventName: string, eventData: any = {}): void {
    if (process.env.NODE_ENV !== 'production') return;
    this.sendAnalytics('/analytics/track', {
      custom_event: eventName,
      event_data: eventData
    });
  }
}

// Newsletter service
class NewsletterService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = 'https://browntech.co/bti-admin-panel/public/api'; // Production API URL
  }

  public async subscribe(email: string, name?: string): Promise<any> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, name })
      });

      return await response.json();
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      throw error;
    }
  }

  public async unsubscribe(email: string): Promise<any> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/newsletter/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      return await response.json();
    } catch (error) {
      console.error('Newsletter unsubscription error:', error);
      throw error;
    }
  }
}

// Contact service
class ContactService {
  private apiBaseUrl: string;
  private sessionId: string;

  constructor() {
    this.apiBaseUrl = 'https://browntech.co/bti-admin-panel/public/api'; // Production API URL
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return 'contact_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public async trackFormEvent(eventType: 'form_started' | 'next_clicked' | 'form_submitted', stepNumber?: number): Promise<void> {
    try {
      // In development, log the event but don't send to server
      if (process.env.NODE_ENV !== 'production') {
        console.log('Contact form event tracked:', { eventType, stepNumber, sessionId: this.sessionId });
        return;
      }

      await fetch(`${this.apiBaseUrl}/contact/track-analytics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          session_id: this.sessionId,
          event_type: eventType,
          step_number: stepNumber !== undefined ? stepNumber : null
        })
      });
    } catch (error) {
      // Silently fail in production to avoid console errors
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to track form event:', error);
      }
    }
  }

  public async submitContactForm(formData: {
    name: string;
    email: string;
    phone?: string;
    company?: string;
    subject: string;
    message: string;
  }): Promise<any> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          session_id: this.sessionId
        })
      });

      return await response.json();
    } catch (error) {
      console.error('Contact form submission error:', error);
      throw error;
    }
  }
}

// Job application service
class JobApplicationService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || 'https://browntech.co/bti-admin-panel/public/api';
  }

  public async submitApplication(formData: {
    job_title: string;
    name: string;
    email: string;
    phone?: string;
    linkedin_url?: string;
    portfolio_url?: string;
    cover_letter: string;
    resume?: File;
  }): Promise<any> {
    try {
      let body: string | FormData;
      let headers: Record<string, string> = {
        'Accept': 'application/json',
      };

      // If there's a resume file, use FormData, otherwise use JSON
      if (formData.resume) {
        const data = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            data.append(key, value);
          }
        });
        body = data;
      } else {
        // Remove resume from the data for JSON
        const { resume, ...jsonData } = formData;
        body = JSON.stringify(jsonData);
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${this.apiBaseUrl}/job-application/submit`, {
        method: 'POST',
        headers,
        body
      });

      if (!response.ok) {
        let errorMessage = 'Failed to submit application';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.errors ? Object.values(errorData.errors).flat().join(', ') : errorMessage;
        } catch (parseError) {
          // If response is not JSON (like PHP error page), get text content
          const errorText = await response.text();
          if (errorText.includes('Content-Length') && errorText.includes('exceeds the limit')) {
            errorMessage = 'File size is too large. Please upload a file smaller than 5MB.';
          } else if (errorText.includes('<')) {
            errorMessage = 'Server error occurred. Please try again or contact support.';
          }
        }
        
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Job application submission error:', error);
      throw error;
    }
  }
}

// Job service for fetching job listings
class JobService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_URL || 'https://browntech.co/bti-admin-panel/public/api';
  }

  public async getJobs(): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/jobs`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch jobs`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
      // Return empty array as fallback
      return [];
    }
  }

  public async getJob(id: number): Promise<any> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/jobs/${id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch job`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching job:', error);
      throw error;
    }
  }
}

// Portfolio service for fetching portfolio projects
class PortfolioService {
  private apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = 'https://browntech.co/bti-admin-panel/public/api';
  }

  public async getProjects(): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/portfolio`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch portfolio projects`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching portfolio projects:', error);
      // Return empty array as fallback
      return [];
    }
  }

  public async getFeaturedProjects(): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/portfolio/featured`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch featured projects`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching featured projects:', error);
      // Return empty array as fallback
      return [];
    }
  }

  public async getProjectsByCategory(category: string): Promise<any[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/portfolio/category/${encodeURIComponent(category)}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch projects by category`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching projects by category:', error);
      // Return empty array as fallback
      return [];
    }
  }

  public async getCategories(): Promise<string[]> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/portfolio/categories`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch categories`);
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Return empty array as fallback
      return [];
    }
  }

  public async getProject(id: number): Promise<any> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/portfolio/${id}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch project`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.error('Error fetching project:', error);
      throw error;
    }
  }
}

// Export services
export const analyticsService = new AnalyticsService();
export const newsletterService = new NewsletterService();
export const contactService = new ContactService();
export const jobApplicationService = new JobApplicationService();
export const jobService = new JobService();
export const portfolioService = new PortfolioService();

// Export types
export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject: string;
  message: string;
}

export interface JobApplicationData {
  job_title: string;
  name: string;
  email: string;
  phone?: string;
  linkedin_url?: string;
  portfolio_url?: string;
  cover_letter: string;
  resume?: File;
}

 